import { oauthProviderClient } from "@better-auth/oauth-provider/client";
import { passkeyClient } from "@better-auth/passkey/client";
import { AuthSessionRequiredError } from "@elefante/auth-ui";
import type {
  ActionResult,
  AuthApi,
  AuthLocale,
} from "@elefante/auth-ui";
import { createAuthClient } from "better-auth/client";

import { buildSignedOAuthQuery } from "./oauth-query";
import { getTurnstileToken } from "./turnstile";

const browserAuth = createAuthClient({
  baseURL: typeof window === "undefined" ? "http://localhost:8787" : window.location.origin,
  basePath: "/api/auth",
  plugins: [oauthProviderClient(), passkeyClient()],
});

interface FetchResult {
  ok: boolean;
  status: number;
  data: Record<string, unknown> | null;
}

export function createUiApi(): AuthApi {
  return {
    async signInPassword(input) {
      const result = await authPost("/sign-in/email", {
        email: input.email,
        password: input.password,
        rememberMe: input.remember,
        ...oauthQueryBody(),
      }, "sign_in");
      return authAction(result, "/account");
    },

    async signUp(input) {
      const result = await authPost("/sign-up/email", {
        name: input.name,
        email: input.email,
        password: input.password,
        locale: input.locale,
        ...oauthQueryBody(),
      }, "sign_up");
      if (result.ok) return { ok: true, message: genericEmailMessage(input.locale) };
      return failedAction(result);
    },

    async signInGoogle(input) {
      const callbackURL = safeReturnUrl(input.returnTo)
        ?? `${window.location.origin}/account`;
      const result = await authPost("/sign-in/social", {
        provider: "google",
        callbackURL,
        ...(input.requestSignUp
          ? {
              requestSignUp: true,
              newUserCallbackURL: callbackURL,
            }
          : {}),
        ...oauthQueryBody(),
      });
      return authAction(result, callbackURL);
    },

    async signInPasskey() {
      const token = await getTurnstileToken("sign_in");
      const response = await browserAuth.signIn.passkey({
        fetchOptions: token ? { headers: { "x-turnstile-token": token } } : {},
      });
      const result = response as unknown as {
        data?: Record<string, unknown> | null;
        error?: { message?: string } | null;
      };
      if (result.error) return { ok: false, message: "Não foi possível usar esta passkey." };
      return {
        ok: true,
        redirectTo: extractRedirect(result.data) ?? "/account",
      };
    },

    async requestMagicLink(input) {
      await authPost("/sign-in/magic-link", {
        email: input.email,
        ...oauthQueryBody(),
      }, "magic_link");
      return { ok: true, message: genericEmailMessage(input.locale) };
    },

    async requestPasswordReset(input) {
      await authPost("/request-password-reset", {
        email: input.email,
        redirectTo: `${window.location.origin}/reset-password`,
        ...oauthQueryBody(),
      }, "password_reset");
      return { ok: true, message: genericEmailMessage(input.locale) };
    },

    async resetPassword(input) {
      return authAction(await authPost("/reset-password", {
        token: input.token,
        newPassword: input.password,
      }), "/sign-in");
    },

    async consumeEmailAction(input) {
      return portalAction("/email-action", "POST", input);
    },

    async respondToConsent(input) {
      const result = await authPost("/oauth2/consent", {
        accept: input.accept,
        ...oauthQueryBody(),
      });
      return authAction(result);
    },

    async getAccount() {
      const response = await fetch("/api/portal/account", { credentials: "same-origin" });
      if (response.status === 401) throw new AuthSessionRequiredError();
      if (!response.ok) throw new Error("account_unavailable");
      return response.json();
    },

    async updateProfile(input) {
      return authAction(await authPost("/update-user", { name: input.name }));
    },

    async requestEmailChange(input) {
      const result = await authPost("/change-email", {
        newEmail: input.email,
        callbackURL: `${window.location.origin}/verify-email`,
      });
      if (result.ok) return { ok: true, message: genericEmailMessage(input.locale) };
      return failedAction(result);
    },

    async reauthenticatePassword(input) {
      const result = await authPost("/sign-in/email", {
        email: input.email,
        password: input.password,
        rememberMe: true,
      }, "sign_in");
      if (!result.ok) return failedAction(result);
      return { ok: true, message: "Identidade confirmada." };
    },

    async reauthenticateGoogle() {
      const callbackURL = `${window.location.origin}/account?reauthenticated=google`;
      const result = await authPost("/sign-in/social", {
        provider: "google",
        callbackURL,
      });
      return authAction(result, callbackURL);
    },

    async reauthenticatePasskey() {
      const token = await getTurnstileToken("sign_in");
      const response = await browserAuth.signIn.passkey({
        fetchOptions: token ? { headers: { "x-turnstile-token": token } } : {},
      });
      const result = response as unknown as {
        error?: { code?: string; message?: string } | null;
      };
      if (result.error) {
        return {
          ok: false,
          ...(result.error.code ? { code: result.error.code } : {}),
          message: "Não foi possível confirmar com esta passkey.",
        };
      }
      return { ok: true, message: "Identidade confirmada." };
    },

    async requestReauthenticationMagicLink(input) {
      await authPost("/sign-in/magic-link", {
        email: input.email,
      }, "magic_link");
      return { ok: true, message: genericEmailMessage(input.locale) };
    },

    async setPassword(input) {
      if (!input.currentPassword) {
        return portalAction("/password/set", "POST", { password: input.password });
      }
      return portalAction("/password/change", "POST", {
        currentPassword: input.currentPassword,
        password: input.password,
      });
    },

    async linkIdentity() {
      const result = await authPost("/link-social", {
        provider: "google",
        callbackURL: `${window.location.origin}/account`,
        errorCallbackURL: `${window.location.origin}/account`,
      });
      return authAction(result);
    },

    async unlinkIdentity(input) {
      return portalAction("/identity/unlink", "POST", input);
    },

    async addPasskey(input) {
      const response = await browserAuth.passkey.addPasskey({ name: input.name });
      const result = response as unknown as {
        error?: { code?: string; message?: string } | null;
      };
      return result.error
        ? {
            ok: false,
            ...(result.error.code ? { code: result.error.code } : {}),
            message: result.error.code === "SESSION_NOT_FRESH"
              ? "Confirme sua identidade antes de alterar os métodos de acesso."
              : "Não foi possível adicionar a passkey.",
          }
        : { ok: true, message: "Passkey adicionada." };
    },

    async renamePasskey(input) {
      return authAction(await authPost("/passkey/update-passkey", {
        id: input.passkeyId,
        name: input.name,
      }));
    },

    async removePasskey(input) {
      return portalAction("/passkey/remove", "POST", input);
    },

    async revokeSession(input) {
      return portalAction("/session/revoke", "POST", input);
    },

    async revokeOtherSessions() {
      return portalAction("/session/revoke-others", "POST", {});
    },

    async logout(input) {
      if (input.allDevices) {
        const result = await portalAction("/logout-all", "POST", {});
        if (!result.ok) return result;
      } else {
        const result = await authPost("/sign-out", {});
        if (!result.ok) return failedAction(result);
      }
      return { ok: true, redirectTo: safeReturnUrl(input.returnTo) ?? "/sign-in" };
    },

    async requestAccountDeletion() {
      return portalAction("/account/deletion", "POST", {});
    },

    async cancelAccountDeletion() {
      return portalAction("/account/deletion", "DELETE");
    },
  };
}

async function authPost(
  path: string,
  body: Record<string, unknown>,
  turnstileAction?: "sign_up" | "sign_in" | "magic_link" | "password_reset",
): Promise<FetchResult> {
  const token = turnstileAction ? await getTurnstileToken(turnstileAction) : null;
  const response = await fetch(`/api/auth${path}`, {
    method: "POST",
    credentials: "same-origin",
    headers: {
      "content-type": "application/json",
      "accept": "application/json",
      ...(token ? { "x-turnstile-token": token } : {}),
    },
    body: JSON.stringify(body),
  });
  return {
    ok: response.ok,
    status: response.status,
    data: await response.json().catch(() => null) as Record<string, unknown> | null,
  };
}

async function portalAction(
  path: string,
  method: "POST" | "DELETE",
  body?: Record<string, unknown>,
): Promise<ActionResult> {
  const response = await fetch(`/api/portal${path}`, {
    method,
    credentials: "same-origin",
    headers: { "content-type": "application/json", "accept": "application/json" },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  const result = await response.json().catch(() => null) as ActionResult | null;
  return result ?? { ok: false, message: "Não foi possível concluir esta ação." };
}

function authAction(result: FetchResult, fallbackRedirect?: string): ActionResult {
  if (!result.ok) return failedAction(result);
  const redirectTo = extractRedirect(result.data) ?? fallbackRedirect;
  return {
    ok: true,
    ...(redirectTo ? { redirectTo } : {}),
  };
}

function failedAction(result: FetchResult): ActionResult {
  if (result.status === 429) return { ok: false, message: "Muitas tentativas. Aguarde e tente novamente." };
  if (result.data?.error === "turnstile_required") return { ok: false, message: "Confirme que você não é um robô e tente novamente." };
  const upstreamCode = result.data?.code ?? result.data?.error;
  const code = typeof upstreamCode === "string" ? upstreamCode : undefined;
  if (code === "SESSION_NOT_FRESH") {
    return {
      ok: false,
      code,
      message: "Confirme sua identidade antes de alterar os métodos de acesso.",
    };
  }
  if (code === "PASSWORD_COMPROMISED") {
    return {
      ok: false,
      code,
      message: "Esta senha aparece em vazamentos conhecidos. Escolha outra senha.",
    };
  }

  return {
    ok: false,
    ...(code ? { code } : {}),
    message: "Não foi possível concluir. Confira os dados e tente novamente.",
  };
}

function extractRedirect(data: Record<string, unknown> | null | undefined): string | undefined {
  const value = data?.url ?? data?.redirectTo ?? data?.redirect_uri;
  return typeof value === "string" ? safeNavigationUrl(value) ?? undefined : undefined;
}

function oauthQueryBody(): { oauth_query: string } | Record<string, never> {
  const query = buildSignedOAuthQuery(currentOAuthQuery());
  return query ? { oauth_query: query } : {};
}

function currentOAuthQuery(): string {
  return typeof window === "undefined" ? "" : window.location.search.replace(/^\?/, "");
}

function safeReturnUrl(value?: string): string | null {
  if (!value) return null;
  try {
    const url = new URL(value, window.location.origin);
    if (url.origin !== window.location.origin) return null;
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return null;
  }
}

function safeNavigationUrl(value: string): string | null {
  try {
    const url = new URL(value, window.location.origin);
    if (url.origin === window.location.origin) return `${url.pathname}${url.search}${url.hash}`;
    if (url.protocol !== "https:") {
      const local = url.hostname === "localhost" || url.hostname === "127.0.0.1" || url.hostname === "[::1]";
      return local ? url.toString() : null;
    }
    if (url.hostname === "accounts.google.com" || /(?:^|\.)elefante\.dev$/i.test(url.hostname)) {
      return url.toString();
    }
    return null;
  } catch {
    return null;
  }
}

function genericEmailMessage(locale: AuthLocale): string {
  return locale === "en"
    ? "If the address can receive this message, the instructions will arrive shortly."
    : "Se o endereço puder receber esta mensagem, as instruções chegarão em breve.";
}
